import { MapLike, ValidTimeZone, stageType, EnvironmentConfigShape, ApiEndPointConfig, StandardProceessEnv, ReactAppProcessEnv } from '@iotv/iotv-v3-types';
import deployedServiceAttributesIn from './deployedServiceAttributes-GENERATED.json';
import { frameworkLicencee } from '../../../frameworkLicenceeConfig';
import { parentAcccountId, parentAccountHostedZoneId, parentHostedZoneName } from '../../../domainAccountConfig';
import { supportedTZs } from '../../../supportedTZs';
import { resolvedEnvironmentVariables } from './resolvedEnvironmentVariables';
import { overrideObjectAttributes } from './overrideObjectAttributes';

type ServiceAttributesMap = MapLike<{
    dev: VariableIdentifiedServiceMap, test: VariableIdentifiedServiceMap, prod: VariableIdentifiedServiceMap
}>
type LambdaDetails = {
    datastoreLayer: {
        arn: string | undefined
    }
}
type DynamoDBDetails = {
    tableName: string | undefined,
    dynamoTableArn: string | undefined,
    LatestStreamArn: string | undefined,
}
type CognitoDetails = {
    USER_POOL_ID: string | undefined,
    USER_POOL_ARN: string | undefined,
    APP_CLIENT_ID: string | undefined,
    IDENTITY_POOL_ID: string | undefined
}
type VariableIdentifiedServiceMap = {
    dynamoDB: DynamoDBDetails;
    cognito: CognitoDetails;
    apiGateway: {
        userGatewayUrl: string | undefined;
        systemManagementUrl: string | undefined;
    };
    iot: { endpointAddress: string | undefined };
    lambdaLayer: LambdaDetails;
    identityCentre?: {
        CreatedDate: string,
        IdentityStoreId: string,
        InstanceArn: string,
        Name: string,
        OwnerAccountId: string,
        Status: string
    }
}

const debug = process.env.REACT_APP_DEBUG === 'true' && true;

/** To be consistent when base config is imported it is imported (within REGEX markers) as
import { config as externalConfig } from './defaultExternalConfig';
*/
import { config as baseConfig } from './defaultExternalConfig'

/** Sub Project imports REGEX MARKER GROUP - no empty lines */
import { config as externalConfig } from '@v020/config';
/** Sub Project imports REGEX MARKER GROUP - exit */

const { NODE_ENV, REACT_APP_PROJECT_CODE, REACT_APP_DEPLOYMENT_STAGE } = process.env as ReactAppProcessEnv;

const isReact = (NODE_ENV === 'development' || NODE_ENV === 'production' || NODE_ENV === 'test') && !!REACT_APP_PROJECT_CODE && !!REACT_APP_DEPLOYMENT_STAGE;
let isDevLocalhost = false;
if (!process.env.AWS_LAMBDA_FUNCTION_NAME && isReact) {
    try {
        if (isReact && window?.location?.hostname === 'localhost') {
            isDevLocalhost = true;
        }
    } catch (e: any) {
        console.error('SUPPRESSED ERR setting isDevLocalhost, (eg where config is being tested locally in terminal). ', e.message)
    }
}

const deployedServiceAttributes = deployedServiceAttributesIn as unknown as ServiceAttributesMap

/** changed 2024 01 not to use destructuring for REACT_APP vars as React Native does not allow access except directly via process.env */
const { default_region, region, accountId, projectCode, appName, stage } = resolvedEnvironmentVariables
const shortProjectCode = ( projectCode ?? REACT_APP_PROJECT_CODE )?.substring(0, 4)
const internalAccountDomain = `${ ( stage ?? REACT_APP_DEPLOYMENT_STAGE) }.${shortProjectCode}.${parentHostedZoneName}`

const undefinedEnvVars = Object.entries({ default_region, accountId, projectCode, appName, stage })
    .reduce((a, [ ck, cv ]) => {
        if (!(typeof cv === 'string')) a.push(ck)
        return a
    }, [] as string[])
if (undefinedEnvVars.length > 0) {
    throw new Error(`The following environment variables are not set ${ undefinedEnvVars.join(', ') }`)
}

/** if the extenal project config ( last project in file:///./../../../externalProjectConfig.ts) has an aws section, 
 * use this as cdk 
 * */
const { aws: _externalProjectCdk, ...externalNonAWS } = externalConfig

/** if externalNonAWS does not specifify BaseData, use default */
externalNonAWS.BaseData = externalConfig.BaseData ?? {}

/** selectively merge aws settings on top of default ---- WARNING THIS HAS BEEN SEEN NOT TO WORK CORRECTLY */
if (baseConfig.aws && externalConfig.aws) {
    overrideObjectAttributes({ overridableObject: baseConfig.aws, overridingObject: externalConfig.aws })
}

const {
    domain,
    weather,
    google,
    apps,
    BaseData,
    apiConfigs,
    iotv,
    mailFromAccountName,
    mailFromSubDomain,
    notificationContentMatrix,
    uiUrls: externalUiUrls,
    ventureTypeExtensions,
    externalDomain,
    notificationObjectHistory
} = externalConfig

const uiUrls = [  `https://*.${internalAccountDomain}`, ...externalUiUrls ?? [] ]

const cdk = baseConfig.aws

const stageAtts = (): VariableIdentifiedServiceMap => {
    let accountStageDeployedServiceAttributes: VariableIdentifiedServiceMap | undefined = undefined;
    try {
        accountStageDeployedServiceAttributes = (deployedServiceAttributes)[ accountId ][ stage as stageType ];
    } catch (e: any) {
        let errMsg: string | undefined = undefined
        if (!(deployedServiceAttributes)[ accountId ]) {
            errMsg = `No entry for accountId ${ accountId }`;
        } else if ((!(deployedServiceAttributes)[ accountId ][ stage as stageType ])) {
            errMsg = `No stage ${ stage } for accountId ${ accountId }`;
        } else {
            errMsg = e.message ?? `unknown error for ${ accountId } ${ stage }`
        }
        console.error(errMsg);
        throw (new Error(errMsg))
    }
    return accountStageDeployedServiceAttributes as VariableIdentifiedServiceMap;
}

const variableStageAtts = stageAtts()
const lwsApiEndpoints: ApiEndPointConfig<string>[] = [
    {
        name: 'NetworkApi',
        endpoint: `https://network.${ internalAccountDomain }`,
        region: default_region,
    },
    {
        name: 'DeviceControlApi',
        endpoint: `https://devicecontrol.${ internalAccountDomain }`,
        region: default_region,
    },
    {
        name: 'SystemManagementApi',
        endpoint: `https://system-management.${ internalAccountDomain }`,
        region: default_region,
    },
    {
        name: 'ConsumerApi',
        endpoint: `https://consumer.${ internalAccountDomain }`,
        region: default_region,
    },
];

const largeObjectIdentifier = 'Big:'

const config = {
    // TODO get into state from https://ipapi.co/json
    // this is semi complete 2025 01, probably reference to config.locale in UI should useIpConfig. Not investigated AWS side usages /
    locale: 'en-NZ',
    frameworkLicencee,
    project: {
        code: projectCode
    },
    stage,
    app: {
        appName,
        appCode: projectCode,
        webUI: {
            ventureName: externalConfig.apps?.webUI?.ventureName,
            useSpecializedViews: externalConfig.apps?.webUI?.useSpecializedViews ?? false,
            initialRedirect: externalConfig.apps?.webUI?.initialRedirect ?? null,
            groupedObjectTypes: externalConfig.apps?.webUI?.groupedObjectTypes,
            domainMap: externalConfig.apps?.webUI?.domainMap,
            showProgrammeSets: externalConfig.apps?.webUI?.showProgrammeSets,
            showPrivacyPolicy: externalConfig.apps?.webUI?.showPrivacyPolicy,
            supportEmail: externalConfig.apps?.webUI?.supportEmail,
            defaultPreferences: {},
        },
        defaultTZ: 'NZ' as ValidTimeZone,
        supportedTZs,
        namespace_uuid: 'a9f2227e-7d5d-4d8a-aecf-4a21cbf951cc',
        ghostUuid: 'xxxxxxxx-xxxx-Mxxx-Nxxx-xxxxxxxxxxxx',
        regex: {
            globalAllow: "^[ A-Za-z0-9$�@:\!_\*,\)\(\-\.\?\+]*$",
            globalDisallow: "pertempter",
            email: '^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$'
        },
        Alert: {
            latestSuffix: '_latestAlert',
            maxHistory: 30,
            noAlertMessage: 'All alerts resolved',
        },
        Shadow: {
            latestSuffix: '_latestShadowState'
        },
        LargeObjects: {
            pk_suffix: largeObjectIdentifier
        },
        attributeLevelSeparator: ':',
        groups: {
            _Admin: '_Admin',
            _Users: '_Users'
        },
        datalifecycle: {
            ttlExpired: {
                days: 31
            },
            calculatedState: {
                days: 100
            }
        },
        geo: {
            S2: {
                defaultLevel: 20
            },
            location: {
                DOP: {
                    minAccepatable: {
                        default: 10,
                    }
                }
            }
        }
    },
    aws: {
        parentAcccountId,
        parentAccountHostedZoneId,
        parentHostedZoneName,
        default_region,
        name_separator: '-',
        accountId,
        nodeRuntime: 'nodejs18.x',
        environments: {
            development: { name: 'dev' },
            testing: { name: 'test' },
            production: { name: 'prod' }
        },
        cdk,
        apiConfigs: [
            ...lwsApiEndpoints,
            ...externalConfig.apiConfigs ?? []
        ],
        cognito: {
            REGION: default_region,
            ...variableStageAtts?.cognito,
            groupDefinitions: externalConfig.aws?.Cognito?.groupDefinitions
        },
        dynamoDB: {
            ...variableStageAtts?.dynamoDB,
            tableName: `${ projectCode }-${ stage }-main-table`,
            apiVersion: '2012-08-10',
            pKey: 'pk',
            sKey: 'sk',
            dpKey: 'dpk',
            dsKey: 'dsk',
            priName: undefined,
            dynamicKeyGSIName: 'DYNAMIC',
            gsiName: 'GSI',
            tsiName: 'TSI',
            typesiName: 'TYPESI',
            typesiPrimaryKey: 'type',
            typesiSortKey: 'name',
            tsiSortKey: 'timestamp',

            ttl: {
                ThingShadowState: 7,
                DeviceControlTransaction: 7,
            },
            batchGetMax: 100,
            batchWriteMax: 25,
            queryLimit: 500
        },
        identityCentre: {
            ...variableStageAtts?.identityCentre
        },
        iot: {
            ...variableStageAtts?.iot
        },
        lambdaLayer: {
            ...variableStageAtts?.lambdaLayer
        },
        mqtt: {
            userChannel: `${ projectCode }/${ stage }/user/[userId]`,
            networkChannel: `${ projectCode }/${ stage }/network/[app_id]`,
            awsDeviceUpdateTopic: '$aws/things/+/shadow/update',
            appDeviceMessageConfirmedTopice: `${ projectCode }/${ stage }/things/thingId/confirmed`,
            customerEntityStateUpdate: `${ projectCode }/${ stage }/customer/[customerSk]/[entitySk]`,
            alertTopic: `${ projectCode }/${ stage }/things/thingId/alert`,
            /** channel for new and updated DCTs */
            deviceControlTransactionChannel: `${ projectCode }/${ stage }/deviceControl/transactions/[deviceSk]`,
            /** channel for Diagnostic message parts */
            deviceControlResponseChannel: `${ projectCode }/${ stage }/deviceControl/responses/[deviceSk]`,
            /** channel for updated DeviceConfigs */
            deviceConfigUpdateChannel: `${ projectCode }/${ stage }/deviceControl/deviceConfig/[deviceSk]`,
            /** IO status, UI clients publish to request the current status */
            ioStatusTopic: `${ projectCode }/${ stage }/io/status`,
            /** IO queue status, UI clients subscribe to see the current status */
            ioQueueTopic: `${ projectCode }/${ stage }/io/queue`,
            /** IO job status, UI clients subscribe to see the current status */
            ioJobTopic: `${ projectCode }/${ stage }/io/job/[userSk]`,
            topic: {
                shadowAccepted: 'shadowAccepted',
                alertIssued: 'alertIssued',
                userChannel: 'userChannel',
                networkChannel: 'network',
                customerChannel: 'customer',
            },
            topicRegex: {
                shadowAccepted: `${ projectCode }/${ stage }/things/.*?/confirmed`,
                alertIssued: `${ projectCode }/${ stage }/things/.*?/alert`,
                userChannel: `${ projectCode }/${ stage }/user/.*?`,
                networkChannel: `${ projectCode }/${ stage }/network/.*?`,
                deviceControl: `${ projectCode }/${ stage }/deviceControl/.*?`,
                customerChannel: `${ projectCode }/${ stage }/customer/.*?`,
            },
        },
        s3: {
            userBlobs: { Name: `${ projectCode }-${ stage }-userblobs` },
            emu: { Name: `${ projectCode }-${ stage }-ui-emu` },
            ui: { Name: `${ projectCode }-${ stage }-ui-version-2` },
            largeObjects: { Name: `${ projectCode }-${ stage }-datalifecycle-large-objects` },
            networkData: { Name: `${ projectCode }-${ stage }-datalifecycle-network-data` },
            networkCoverage: { Name: `${ projectCode }-${ stage }-network-coverage` },
            io: { Name: `${ projectCode }-${ stage }-io-jobs` }
        },
        sns: {
            mainTableStreamTopicArn: `arn:aws:sns:${ default_region }:${ accountId }:${ projectCode }-${ stage }-DynStr-Topic`,
        },
        sqs: {
            maxDelay: 180,
            subscriptionEvaluationQueue: {
                queueArn: `arn:aws:sqs:${ region }:${ accountId }:${ projectCode }-${ stage }-subscriber-notification-queue`,
                queueUrl: `https://sqs.${ region }.amazonaws.com/${ accountId }/${ projectCode }-${ stage }-subscriber-notification-queue`
            },
            notificationEmitterQueue: {
                queueArn: `arn:aws:sqs:${ region }:${ accountId }:${ projectCode }-${ stage }-notification-emit-queue`,
                queueUrl: `https://sqs.${ region }.amazonaws.com/${ accountId }/${ projectCode }-${ stage }-notification-emit-queue`
            },
            networkDataQueue: {
                queueArn: `arn:aws:sqs:${ region }:${ accountId }:${ projectCode }-${ stage }-networkDataIngestion`,
                queueUrl: `https://sqs.${ region }.amazonaws.com/${ accountId }/${ projectCode }-${ stage }-networkDataIngestion`
            }
        },
    },
    domain,
    internalAccountDomain,
    externalDomain,
    weather,
    google,
    apps,
    uiUrls,
    BaseData,
    apiConfigs,
    iotv,
    mailFromSubDomain,
    notificationContentMatrix,
    ventureTypeExtensions,
    notificationObjectHistory,
    mailFromAccountName
    /* TODO - required by SES under an assertion - currently SES has an Omit to avoid this */
    // parentAccountDomain: parentHostedZoneName,
}

if (isDevLocalhost) {
    config.google.gcp = baseConfig.google.gcp
}

export default config;
